/**
 * liveSessionActions — les gestes qu'une séance permet, dérivés de son état.
 *
 * Une règle pure, lue par deux surfaces : la feuille d'actions d'une séance
 * (`<LiveSessionActionsSheet>`, qui en tire ses lignes) et « Quelle séance ? » des gestes rapides
 * (`pickableSessions`, qui propose les séances dont la feuille offre le geste).
 */
import {
    canJoinLiveSession,
    canMoveSession,
    isAbsenceReported,
    isGroupSession,
    isPaymentBlocked,
    isSessionFinished,
} from "./liveSessionFormatter"
import { type UILiveSession } from "../types"

/**
 * Un geste possible sur une séance donnée.
 *
 * `move_session` / `report_absence` sont les deux faces de la même intention (« il ne sera pas
 * là ») : la première quand la séance est encore déplaçable, la seconde à moins de 2 h, où le
 * backend accepte le drapeau d'absence mais plus le rattrapage.
 */
export type LiveSessionAction =
    | "join_as_child"
    | "move_session"
    | "report_absence"
    | "report_late"
    | "message_tutor"
    | "cancel_absence"
    | "cancel_late"
    | "cancel_recovery"

/**
 * Les gestes offerts, dérivés de l'état de la séance — mêmes règles que `visibleActions()` du
 * menu legacy, plus la coupure des 2 h :
 *  - paiement en attente : rien, pour le parent comme pour l'élève — les séances sont suspendues,
 *    et l'appelant propose la régularisation (`onBlockedSessionPress`) plutôt que d'ouvrir une
 *    feuille. L'élève y perd « Décaler » et « Signaler un retard », que le serveur lui refuse tant
 *    que le paiement n'est pas réglé : il ne lui reste que la boîte du paiement (Wajih, 19/10) ;
 *  - séance terminée : rien (parité legacy) ;
 *  - absence déjà notée : on ne peut plus que la défaire, ou écrire au tuteur ;
 *  - retard déjà signalé : inutile de le re-signaler, mais la séance reste déplaçable ;
 *  - à moins de 2 h : « Prévenir d'une absence » remplace « Décaler cette séance » — annoncer un
 *    geste impossible pour le refuser à l'écran suivant, c'est une impasse déguisée.
 *  - un cours qui n'est pas en groupe (individuel, essai, soutien) : ni absence ni retard, à
 *    aucune heure. Il se règle avec quelqu'un — « Décaler cette séance » y mène à « contactez le
 *    tuteur » (l'équipe pour un essai). Un drapeau posé là laisserait le tuteur attendre seul :
 *    à moins de 2 h, le backend l'accepte sans prévenir personne.
 *
 * `joinAsChild` arrive de l'appelant : c'est lui qui sait si l'enfant a un jeton de bascule. Quand
 * il est vrai, « Rejoindre » passe **en tête** — à l'heure du live, c'est le geste attendu, et
 * c'est le seul de la liste qui se périme dans l'heure. `canJoinLiveSession` tranche le reste
 * (jour même, séance ni clôturée ni annulée), et elle écarte déjà `finished` / `absent`, où les
 * retours anticipés ci-dessus ont de toute façon la main.
 */
export function sessionActions(session: UILiveSession, joinAsChild = false): LiveSessionAction[] {
    if (isPaymentBlocked(session)) return []
    const join: LiveSessionAction[] = joinAsChild && canJoinLiveSession(session) ? ["join_as_child"] : []

    if (isSessionFinished(session)) return []
    if (isAbsenceReported(session)) return ["cancel_absence", "message_tutor"]

    const group = isGroupSession(session)
    const move: LiveSessionAction = !group || canMoveSession(session) ? "move_session" : "report_absence"
    const late: LiveSessionAction[] = group ? ["report_late"] : []

    if (session.statuses.includes("late")) return [...join, move, "message_tutor", "cancel_late"]
    if (session.statuses.includes("makeup_recovery"))
        return [...join, move, ...late, "message_tutor", "cancel_recovery"]
    return [...join, move, ...late, "message_tutor"]
}
